/* ==========================================================================
   ef-pdp-mantels-companion.js
   §R9-H17 / §R9-H18 / §R9-H15 — ROUND 9, 2026-09-03.

   A READ-ONLY companion to assets/ef-pdp-mantels.js, which is FROZEN and is not
   opened, called, patched or monkey-patched by this file. Everything below is
   one of exactly three operations:

     * read the DOM the engine has already rendered
     * set `textContent` / `hidden` on a node the engine does not know about
       (`#efmLenBoundary`, `#efmReviewSummary`, `#efmLenLadder[data-ladder-ok]`)
     * set `data-phase` on `#efPd2Sticky`

   It never adds a cart item, never computes or writes a price the engine owns,
   never dispatches an event the engine listens for, and never removes a class
   the engine sets. If this file fails to load, the page is exactly the page it
   was before it existed: the ladder is static Liquid, the boundary line and the
   summary line stay `hidden`, and the order bar's paint rules key off `:has()`
   rather than off `data-phase` precisely so that money never depends on a
   script that might not arrive.
   ========================================================================== */
(function () {
  'use strict';

  var $ = function (id) { return document.getElementById(id); };
  var money = function (n) { return '$' + Number(n).toLocaleString('en-US'); };

  /* WRITE ONLY WHEN THE VALUE CHANGES — this is not a micro-optimisation, it is
     the difference between a companion and an infinite loop. The observer below
     watches #efmWrap with `childList: true, subtree: true`, and both nodes this
     file writes live inside #efmWrap. Assigning `textContent` replaces the text
     node even when the string is identical, which produces a mutation record,
     which schedules another run, which writes again — a rAF loop that would
     never settle and would sit at 60 fps on a phone battery for as long as the
     review is open. Every write in this file goes through here. */
  function setText(el, text) {
    if (!el) return;
    var want = text == null ? '' : String(text);
    if (el.textContent === want) return;
    el.textContent = want;
  }
  function setHidden(el, v) { if (el && el.hidden !== v) el.hidden = v; }
  function setAttr(el, k, v) { if (el && el.getAttribute(k) !== v) el.setAttribute(k, v); }

  /* ------------------------------------------------------------------------
     THE PRICEBOOK, READ OFF THE PAGE.
     Every <option> in #efmLengthSelect carries data-price. Bands are runs of
     equal price over consecutive inches. Deriving them here rather than
     hard-coding them is the whole reason a drift between the static ladder and
     the pricebook can be detected instead of shipped.
     ------------------------------------------------------------------------ */
  function bands() {
    var sel = $('efmLengthSelect');
    if (!sel) return [];
    var out = [], cur = null;
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      if (!o.value) continue;
      var inch = parseInt(o.value, 10);
      var price = parseInt(o.getAttribute('data-price') || '0', 10);
      if (!isFinite(inch)) continue;
      if (!cur || cur.amt !== price || inch !== cur.to + 1) { cur = { from: inch, to: inch, amt: price }; out.push(cur); }
      else cur.to = inch;
    }
    return out;
  }

  /* ------------------------------------------------------------------------
     1. LADDER DRIFT CHECK (H17 gate).
     The static ladder names five bands and four amounts. If the pricebook ever
     moves, `data-ladder-ok` goes to "0" and the gate is RED — instead of the
     page quietly telling a buyer the wrong number.
     ------------------------------------------------------------------------ */
  function checkLadder(bs) {
    var el = $('efmLenLadder');
    if (!el) return;
    var txt = el.textContent.replace(/\s+/g, ' ');
    var want = [
      { re: /20.{1,3}30.{1,3} included/, ok: bs.length > 0 && bs[0].from === 20 && bs[0].to === 30 && bs[0].amt === 0 },
      { re: /31.{1,3}50.{1,3} \+\$135/, ok: has(bs, 31, 50, 135) },
      { re: /51.{1,3}70.{1,3} \+\$325/, ok: has(bs, 51, 70, 325) },
      { re: /71.{1,3}89.{1,3} \+\$725/, ok: has(bs, 71, 89, 725) },
      { re: /90.{1,3}\+ from \+\$1,350/, ok: firstAt(bs, 90) === 1350 },
    ];
    var ok = want.every(function (w) { return w.re.test(txt) && w.ok; });
    setAttr(el, 'data-ladder-ok', ok ? '1' : '0');
  }
  function has(bs, from, to, amt) {
    for (var i = 0; i < bs.length; i++) if (bs[i].from === from && bs[i].to === to && bs[i].amt === amt) return true;
    return false;
  }
  function firstAt(bs, inch) {
    for (var i = 0; i < bs.length; i++) if (bs[i].from === inch) return bs[i].amt;
    return null;
  }

  /* ------------------------------------------------------------------------
     2. THE BAND-BOUNDARY LINE (H17b).
     Shown only within 2" of a band edge, which is the window in which one inch
     is worth real money and the buyer cannot see it: the <option> labels show
     the band's own surcharge, never the STEP between bands.

     TWO SENTENCES, NOT ONE, AND THE REASON IS ON THE RECORD.
     §8 licenses `50" is the last inch in this band. 51" adds $190.` — which is
     true when the buyer is standing at 49 or 50. The gate also wants the line
     at 51, where that sentence is in the wrong tense: they have already crossed.
     Rather than show a false sentence to pass a gate, the crossed case gets the
     same fact with the tense corrected and nothing added:
       `50" is the last inch in the band below. 51" added $190.`
     Flagged in logs/R9-MANTEL.md as a deviation from the literal copy row.
     ------------------------------------------------------------------------ */
  function boundary(bs) {
    var el = $('efmLenBoundary'), sel = $('efmLengthSelect');
    if (!el || !sel) return;
    var len = parseInt(sel.value, 10);
    if (!isFinite(len)) { setText(el, ''); setHidden(el, true); return; }
    for (var i = 0; i < bs.length - 1; i++) {
      var edge = bs[i].to, step = bs[i + 1].amt - bs[i].amt;
      if (step <= 0) continue;
      if (len >= edge - 2 && len <= edge + 2) {
        setText(el, len <= edge
          ? edge + '" is the last inch in this band. ' + (edge + 1) + '" adds ' + money(step) + '.'
          : edge + '" is the last inch in the band below. ' + (edge + 1) + '" added ' + money(step) + '.');
        setHidden(el, false);
        return;
      }
    }
    setText(el, ''); setHidden(el, true);
  }

  /* ------------------------------------------------------------------------
     3. THE REVIEW'S ONE-LINE DIMENSION SUMMARY (H18).
     Built from the three dimension ROWS the engine has already rendered, so it
     cannot disagree with them: if the engine changes what it writes, this line
     changes with it or disappears. It is never built from the selects, which
     would be a second source of truth for the same fact.
     ------------------------------------------------------------------------ */
  /* Straight " and not the Prime character, because the two sit eight pixels
     apart on screen: the dimension cards' own hints render `20" - 144"` and the
     boundary line renders `50" is the last inch...`. Looked at the 390 capture,
     saw the mismatch, matched the neighbour. It is also the form the spec's
     copy rows are written in. */
  var DIMS = [
    { key: /shelf\s*length/i, word: 'long' },
    { key: /shelf\s*height/i, word: 'high' },
    { key: /shelf\s*depth/i, word: 'deep' },
  ];
  function summary() {
    var host = $('efmReviewSummary'), table = $('efmReviewTable');
    if (!host || !table) return;
    var parts = [];
    for (var d = 0; d < DIMS.length; d++) {
      var found = null;
      var rows = table.children;
      for (var r = 0; r < rows.length; r++) {
        var t = (rows[r].textContent || '').replace(/\s+/g, ' ');
        if (!DIMS[d].key.test(t)) continue;
        var m = t.match(/(\d+(?:\s*\d*\/\d+)?)\s*["″”]/);
        if (m) found = m[1].replace(/\s+/g, ' ').trim();
        break;
      }
      if (!found) { setText(host, ''); setHidden(host, true); return; }
      parts.push(found + '" ' + DIMS[d].word);
    }
    setText(host, parts.join(' × '));
    setHidden(host, false);
  }

  /* ------------------------------------------------------------------------
     4. #efPd2Sticky[data-phase] (H15's portable key).
     Published for readers — gates, later tasks, and any surface that cannot use
     `:has()`. The order bar's own paint rule does NOT consume it: a stale
     "commit" here would repaint a buy button on the colour step, and that is
     the exact defect H15 exists to remove. Written from the same DOM state the
     selector reads, on the same frame.
     ------------------------------------------------------------------------ */
  function phase() {
    var bar = $('efPd2Sticky');
    if (!bar) return;
    var vis = document.querySelector('.ef-cfg-mantel-step.is-visible');
    var step = vis ? (vis.getAttribute('data-step') || '') : '';
    var v = !vis ? 'entry' : (step === '4' ? 'commit' : (step === 'success' ? 'done' : 'building'));
    setAttr(bar, 'data-phase', v);
  }

  /* ------------------------------------------------------------------------
     WIRING. One MutationObserver on the wizard root plus the select's own
     change event. No polling, no timers, no listeners on anything the engine
     owns. `subtree: true` on #efmWrap is the cheapest correct scope: the engine
     re-renders the review table wholesale, so a childList observer on the table
     alone would miss a replaced table.
     ------------------------------------------------------------------------ */
  function run() {
    var bs = bands();
    checkLadder(bs);
    boundary(bs);
    summary();
    phase();
  }

  function boot() {
    var wrap = $('efmWrap');
    if (!wrap) return;
    run();
    var sel = $('efmLengthSelect');
    if (sel) sel.addEventListener('change', function () { boundary(bands()); }, { passive: true });
    var queued = false;
    var mo = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; run(); });
    });
    mo.observe(wrap, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-step'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
